<!-- @format -->

<template>
  <div id="app">
    <div
      style="
        width: 100%;
        height: 100%;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        position: fixed;
        transition: ease-in-out 0.5s;
        z-index: 2500;
        justify-content: center;
        align-items: center;
        background-color: rgba(0, 0, 0, 0.2);
      "
      :style="{ display: isLoading ? 'flex' : 'none' }"
      aria-hidden="true"
    >
      <div style="border-radius: 5px">
        <div class="spinner-border text-light"></div>
      </div>
    </div>
    <!-- The spinner carried role="status" with nothing to read, inside an
         overlay that is display:none between requests — so it was out of the
         accessibility tree at the moment it would have had something to
         announce (outstanding-2q50). The overlay is decoration; the status
         region is always rendered and its text is what changes. -->
    <div class="visually-hidden" role="status">
      {{ isLoading ? "Loading" : "" }}
    </div>
    <component :is="route.meta.layout">
      <RouterView></RouterView>
    </component>
  </div>
</template>

<script setup lang="ts">
import { useRoute } from "vue-router";
import { useLoading } from "./composables/useLoading";
import { useDocumentHead } from "./composables/useDocumentHead";
import { ensureExperimentAssignments } from "./utils/events";

const route = useRoute();
const { isLoading } = useLoading();

// Fetch experiment assignments once per load, at app level, so every entry
// point (direct photo/collection/cart landings, not just the homepage) can
// resolve payload surfaces and carry exposure tokens (bead zpe.7).
// Fire-and-forget: payload consumers await ensureExperimentAssignments()
// themselves; the memoized promise means no second request.
void ensureExperimentAssignments();

// Bind the shared document-head state to the document.
useDocumentHead();
</script>

<style>
@charset "UTF-8";

.form-group {
  padding-bottom: 10px;
}

#addressVerificationModal {
  padding-right: 0px !important;
}

.text-right {
  text-align: right;
}

.text-center {
  text-align: center;
}

.text-left {
  text-align: left;
}

h1 {
  font-size: 2rem;
}

h2 {
  font-size: 1.5rem;
}

/* Rainbow gradient-text on the wordmark. Rewritten from the 2011-era
   `-webkit-gradient(... color-stop ...)` legacy syntax (which modern browsers
   stopped honoring — leaving no gradient and, with color:transparent, an
   invisible/greyed wordmark) to the standard `linear-gradient()`. The
   `.rainbow.router-link-exact-active` selector (specificity 0,2,0) is included so
   the effect survives on the HOMEPAGE, where the logo is the active route and
   `.router-link-exact-active { color:#333 }` would otherwise repaint it grey. */
.rainbow,
.rainbow:hover,
.rainbow.router-link-exact-active {
  display: inline-block;
  background-image: linear-gradient(
    to right,
    red 0%,
    orange 15%,
    yellow 30%,
    green 45%,
    blue 65%,
    indigo 80%,
    violet 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

html {
  position: relative;
  min-height: 100%;
}

body {
  background-color: #ffffff;
  margin-bottom: 30px;
}

h1.logo {
  font-weight: 300;
  letter-spacing: 3px;
  display: inline-block;
  margin-bottom: 0;
  position: relative;
  top: 7px;
}

.logo-divider {
  border-top-color: rgb(81, 81, 81);
  margin-top: 0;
}

.router-link-exact-active {
  color: #333;
  text-decoration: underline !important;
}

.icon-buttons {
  color: #333;
}

.icon-buttons:hover {
  color: #000;
}

/* Footer type scale: one small-but-legible base for the whole footer region.
   Was 0.5rem (~8px), which some children escaped with their own sizes (signup
   label 0.85rem, form controls 1rem) while the nav links and copyright inherited
   it and collapsed to ~8px — a jumpy, uneven hierarchy. 0.8rem is the fine-print
   tier (nav links); the signup label steps up from here and the copyright steps
   down, so the footer reads as a deliberate scale. */
.footer {
  padding-top: 5px;
  text-align: center;
  font-size: 0.8rem;
}

.footer-links {
  margin-bottom: 0;
}

.footer-link {
  text-decoration: none !important;
  color: #6c757d;
}

.footer-link:hover {
  text-decoration: underline !important;
  color: #333;
}
</style>
